import React, { useState } from "react";
import { Layout, Menu, Button, Space, Breadcrumb } from "antd";
import {
  LogoutOutlined,
  TeamOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HistoryOutlined,
  CrownOutlined,
  FileTextOutlined,
  BookOutlined,
  ProjectOutlined,
  ApartmentOutlined,
  StarOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthService } from "../../services";
import TokenManager from "../../utils/tokenManager";
import ErrorHandler from "../../utils/errorHandler";
import "../../styles/MainLayout.scss";

const { Header, Sider, Content } = Layout;

// 菜单配置
const menuItems = [
  {
    key: "users",
    path: "/users",
    icon: <TeamOutlined />,
    label: "账号管理",
  },
  {
    key: "login-records",
    path: "/login-records",
    icon: <HistoryOutlined />,
    label: "登录记录",
  },
  {
    key: "token-config",
    path: "/token-config",
    icon: <CrownOutlined />,
    label: "Token配置",
  },
  {
    key: "token-records",
    path: "/token-records",
    icon: <FileTextOutlined />,
    label: "Token记录",
  },
  {
    key: "tasks",
    path: "/tasks",
    icon: <ProjectOutlined />,
    label: "任务管理",
  },
  {
    key: "dict",
    path: "/dict",
    icon: <BookOutlined />,
    label: "字典管理",
  },
  {
    key: "orgs",
    path: "/orgs",
    icon: <ApartmentOutlined />,
    label: "组织管理",
  },
  {
    key: "vip",
    path: "/vip",
    icon: <StarOutlined />,
    label: "VIP会员管理",
  },
  {
    key: "stat",
    path: "/stat",
    icon: <LineChartOutlined />,
    label: "数据统计",
  },
];

// 面包屑映射配置
const breadcrumbMap: Record<string, string[]> = {
  "/users": ["首页", "账号管理"],
  "/login-records": ["首页", "登录记录"],
  "/token-config": ["首页", "Token配置"],
  "/token-records": ["首页", "Token记录"],
  "/tasks": ["首页", "任务管理"],
  "/dict": ["首页", "字典管理"],
  "/orgs": ["首页", "组织管理"],
  "/vip": ["首页", "VIP会员管理"],
  "/stat": ["首页", "数据统计"],
};

interface MainLayoutProps {
  children: React.ReactNode;
  title?: string;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, title }) => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // 获取当前用户角色
  const getCurrentUserRole = (): string[] => {
    const token = TokenManager.getAccessToken();
    if (!token) return [];

    const userInfo = TokenManager.getUserFromToken(token);
    return userInfo?.roles || [];
  };

  // 根据角色过滤菜单项
  const getFilteredMenuItems = () => {
    const userRoles = getCurrentUserRole();
    const isAdmin = userRoles.includes("admin");

    // 只有 admin 才能看到的菜单项
    const adminOnlyMenuKeys = [
      "users",
      "login-records",
      "token-config",
      "dict",
      "orgs",
      "vip",
      "stat",
    ];

    return menuItems.filter((item) => {
      // 如果是 admin 专用菜单，只有 admin 才能看到
      if (adminOnlyMenuKeys.includes(item.key)) {
        return isAdmin;
      }
      // 其他菜单项所有用户都可以看到
      return true;
    });
  };

  // 处理登出
  const handleLogout = async () => {
    try {
      // 调用登出 API
      await AuthService.logout();
    } catch (error) {
      ErrorHandler.handleApiError(error, false);
    } finally {
      // 清除本地存储的 token
      TokenManager.clearToken();
      navigate("/login");
    }
  };

  // 获取当前路径的面包屑
  const getBreadcrumbs = () => {
    const breadcrumbItems = breadcrumbMap[location.pathname] || ["首页"];

    const items = breadcrumbItems.map((item, index) => ({
      title: item,
      key: index,
    }));

    return <Breadcrumb style={{ margin: "16px 0" }} items={items} />;
  };

  // 获取当前选中的菜单项
  const getSelectedKey = () => {
    const filteredItems = getFilteredMenuItems();
    // 从过滤后的菜单配置中查找匹配的路径
    const currentMenu = filteredItems.find(
      (item) => item.path === location.pathname
    );
    return currentMenu ? currentMenu.key : "";
  };

  return (
    <Layout
      className="main-layout"
      style={{ height: "100vh", overflow: "hidden" }}
    >
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        className="main-sider"
        trigger={null} // 移除默认的触发器
        style={{
          overflow: "auto",
          height: "100vh",
          position: "fixed",
          left: 0,
        }}
      >
        <div className="logo">
          {collapsed ? (
            <img
              style={{
                width: 50,
                height: 50,
              }}
              src="https://static.pqmagic.cn/magic-web/img/icon.png"
              alt=""
            />
          ) : (
            <span>后台管理系统</span>
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          items={getFilteredMenuItems().map((item) => ({
            key: item.key,
            icon: item.icon,
            label: item.label,
            onClick: () => navigate(item.path),
          }))}
        />
      </Sider>

      <Layout
        style={{ marginLeft: collapsed ? 80 : 200, transition: "all 0.2s" }}
      >
        <Header
          className="main-header"
          style={{ position: "sticky", top: 0, zIndex: 1, width: "100%" }}
        >
          <div className="header-content">
            <div className="header-left">
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                className="trigger-btn"
              />
              <h1>{title || "管理系统"}</h1>
              {getBreadcrumbs()}
            </div>
            <div className="header-right">
              <Space>
                <Button
                  type="link"
                  icon={<LogoutOutlined />}
                  onClick={handleLogout}
                  className="logout-btn"
                >
                  注销
                </Button>
              </Space>
            </div>
          </div>
        </Header>

        <Content
          className="main-content"
          style={{
            overflow: "auto",
            padding: "24px",
            height: "calc(100vh - 64px)",
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
